import get from 'lodash/get';
import capitalize from 'lodash/capitalize';
import isFinite from 'lodash/isFinite';
import isEmpty from 'lodash/isEmpty';
import { camelizeKeys } from 'humps';
import { isValidDate } from 'utils/utils';
import {
  PRODUCT_AVAILABILITY_STATUSES,
  PRODUCT_TAG_CATEGORIES,
  PROP_TYPE_ALTERNATIVE_PRODUCT,
  PROP_TYPE_CUSTOM_PRODUCT
} from 'utils/constants';
import { RETURN_POLICY } from 'utils/constants/productDetailsPage';

import { red, black, dawnPink } from 'web-ui-kit/colors';

const PRODUCT_BADGE_EXACTMATCH = 'Your exact piece';
const PRODUCT_BADGE_STANDIN = 'Stand-in';
const PRODUCT_BADGE_SALE = 'Sale';
const PRODUCT_BADGE_NEW = 'New';

// These are the slugs for product types that should be displayed but with special
// treatment as unsellable.
const UNSELLABLE_TYPES = ['paint-color', 'Paint Color'];

/**
 * We should consolidate as much of the business logic
 * around Products as we can here. If you find yourself
 * using multiple product attributes to perform or gate
 * an action, then that logic should probably be delegated
 * as a helper method in this module.
 */
/**
 * These are the product properties we care about.
 */
const defaultProps = {
  id: null,
  color: '',
  currentPrice: null,
  customPropType: null, // used if this is a custom prop
  designId: null, // We attach design IDs to products in cart where possible
  externalId: '',
  has3dAssets: false,
  images: [],
  isFavorite: false,
  isMaterial: false,
  material: '',
  name: '',
  availability: null,
  availabilityInfo: null,
  isOutOfStock: false,
  isMadeToOrder: false,
  isBackOrdered: false,
  regularPrice: null,
  vendorPrice: null,
  modsySavings: null,
  source: '',
  sourceStatus: '',
  tags: [],
  types: [],
  url: '',
  additionalInfo: {},
  isTooBig: false,

  // dimensions
  depth: 0,
  height: 0,
  width: 0
};

/** Helper function.
 * Maps the warehouse Product instance fields to our internal fields.
 */
const convertInternal = (whProduct) => {
  let pricePoint;
  let color = '';
  let material = '';
  const tagsDict = whProduct.tags_dict || whProduct.tagsDict;
  if (tagsDict) {
    pricePoint = get(tagsDict, 'pricePoint[0]', undefined);
    color = get(tagsDict, 'Color[0]', '');
    material = get(tagsDict, 'Material[0]', '');
  }
  // Get variant attributes
  const allAttributes = get(whProduct, 'variant_attributes') || get(whProduct, 'variantAttributes', {});
  // Pull out primary variant attribute - used to control what other attributes are available
  let primaryVariantAttribute = '';
  let variantAttributes = {};
  if (allAttributes) {
    const { primary_attribute: whPrimaryAttribute, primaryAttribute, ...rest } = allAttributes;
    primaryVariantAttribute = whPrimaryAttribute || primaryAttribute;
    variantAttributes = rest;
  }

  return {
    id: whProduct.id,
    color: whProduct.color || color,
    currentPrice: parseFloat(
      whProduct.current_price ||
      whProduct.currentPrice ||
      whProduct.promo_price ||
      whProduct.promoPrice ||
      whProduct.price
    ),
    customPropType: parseInt(whProduct.custom_prop_type) || null,
    description: whProduct.description || '',
    designId: whProduct.designId || null,
    externalId: whProduct.external_id || whProduct.externalId || '',
    has3dAssets: get(whProduct, 'assets.length', 0) > 0,
    images: whProduct.images || [],
    availability: whProduct.availability || null,
    availabilityInfo: whProduct.availability_info || whProduct.availabilityInfo || null,
    isFreeShipping: whProduct.has_free_shipping || false,
    isMaterial: getIsMaterial(whProduct),
    isOutOfStock: whProduct.out_of_stock || whProduct.outOfStock || false,
    material: whProduct.material || material,
    name: whProduct.name || '',
    regularPrice: parseFloat(whProduct.price) || null,
    shippingQuote: whProduct.shippingInfoQuote || whProduct.shipping_info_quote,
    shippingCost: isFinite(whProduct.shippingInfoCost)
      ? whProduct.shippingInfoCost : whProduct.shipping_info_cost,
    shippingPolicy: whProduct.shippingInfoPolicy || whProduct.shipping_info_policy || RETURN_POLICY,
    source: whProduct.source || '',
    sourceStatus: whProduct.source_status || whProduct.sourceStatus || '',
    specialMessaging: whProduct.special_messaging || '',
    styleTag: get(whProduct.tags_dict, 'Style') || get(whProduct.tagsDict, 'Style'),
    genomeTag: get(whProduct.tags_dict, 'Genome') || get(whProduct.tagsDict, 'Genome'),
    internalTags: get(whProduct.tags_dict, 'Internal') || get(whProduct.tagsDict, 'Internal', []),
    tags: whProduct.tags || [],
    types: whProduct.types || [],
    mainType: getProductType(whProduct),
    uiMessaging: camelizeKeys(get(whProduct, 'ui_messaging')),
    url: whProduct.url || '',
    variantAttributes: variantAttributes,
    primaryVariantAttribute: primaryVariantAttribute,
    variantHash: get(whProduct, 'variant_hash') || get(whProduct, 'variantHash', ''),
    vendorPrice: parseFloat(whProduct.vendorPrice || whProduct.vendor_price || whProduct.price),
    modsySavings: parseFloat(whProduct.modsySavings || whProduct.modsy_savings),
    vendorType: whProduct.vendorType || whProduct.vendor_type || null,

    // additional product info for PDP
    additionalInfo: camelizeKeys(whProduct.additionalInfo || whProduct.additional_info || {}),

    // dimensions
    depth: whProduct.depth || 0,
    height: whProduct.height || 0,
    width: whProduct.width || 0,

    pricePoint,
    isTooBig: whProduct.too_big || whProduct.tooBig || false,
    // Used to determine whether we have received all variants from the BE
    variantsLoaded: false,

    quantityMultiple: get(whProduct, 'quantity_multiple') || get(whProduct, 'quantityMultiple') || 1
  };
};

function getIsMaterial (productData) {
  return UNSELLABLE_TYPES.includes(productData.product_type) || UNSELLABLE_TYPES.includes(productData.productType);
}

/** Helper Function
 * Figure out the type of the product
 */

function getProductType (productData) {
  let type = '';

  if (productData.product_type || productData.productType) {
    return productData.product_type || productData.productType;
  }

  if (productData.types) {
    for (let idx = 0; idx < productData.types.length; idx++) {
      if (productData.types[idx] && productData.types[idx].indexOf('Custom Product') < 0) {
        type = productData.types[idx];
        break;
      }
    }
  }

  if (get(productData, 'tags.Type')) {
    for (let idx = 0; idx < productData.tags.Type.length; idx++) {
      if (productData.tags.Type[idx] && productData.tags.Type[idx].indexOf('Custom Product') < 0) {
        type = productData.tags.Type[idx];
        break;
      }
    }
  }

  if (type.length === 0) {
    // This means we have no type, which can happen with custom products that are only tagged as custom
    // products. Let's see if we can guess the type from the name:
    for (let key in PRODUCT_TAG_CATEGORIES) {
      let cappedKey = key.split(' ').map(word => capitalize(word)).join(' ');

      if (get(productData, 'name', '').indexOf(cappedKey) >= 0) {
        type = cappedKey;
        break;
      }
    }
  }

  return type;
}

/**
 * We should consolidate as much of the business logic
 * around Products as we can here. If you find yourself
 * using multiple design attributes to perform or gate
 * an action, then that logic should probably be delegated
 * as a helper method in this module.
 */
class Product {
  /** This is the StyleboardProduct instance which includes
   * the product instance as well as additional metadata.
   */
  static createProductFromWarehouseResponse ({ whProduct }) {
    return new Product({
      ...convertInternal(whProduct.product),
      isFavorite: whProduct.is_favorite || false,
      whProduct: whProduct.product
    });
  }

  /** This is the pure Product instance from server. */
  static createProductFromWarehouseProductResponse ({ whProduct }) {
    return new Product({
      ...convertInternal(whProduct),
      whProduct
    });
  }

  /** Creates a new product instance with defaults and optional overrides. */
  constructor (overrides) {
    this.fields = { ...defaultProps, ...overrides };
  }

  /** Creates a new product copied from this with optional overrides. */
  makeCopy (overrides) {
    return new Product({ ...this.fields, ...overrides });
  }

  doesProductMatchFilter = (filterValues, designs, purchasedIds) => {
    if (this.currentPrice() <= 0 && this.regularPrice() <= 0) return false;
    let match = true;

    Object.keys(filterValues).forEach(key => {
      // ignore if there are no values
      if (filterValues[key].length > 0) {
        let categoryMatch = false;

        filterValues[key].forEach(value => {
          if (!value.value) { value.value = value.name; }

          switch (key) {
            case 'Category':
              // TODO: How can we find if a product matches a category?
              categoryMatch = categoryMatch || (
                (this.fields.types && this.fields.types.includes(value.value)) ||
                  (this.fields.tags && this.fields.tags.includes(value.value)));
              break;
            case 'Brand':
              categoryMatch = categoryMatch || (this.fields.source === value.value);
              break;
            case 'Price':
              categoryMatch = categoryMatch || (this.currentPrice() >= value.value[0] && (this.currentPrice() < value.value[1] || value.value[1] < 0));
              break;
            case 'Material':
            case 'Color':
              categoryMatch = categoryMatch || (this.fields.tags.includes(value.value));
              break;
            case 'Purchased':
              let purchased = purchasedIds.includes(this.fields.id);
              categoryMatch = categoryMatch || (purchased === (value.value.toLowerCase() === 'purchased through modsy'));
              break;
            case 'View Products From':
              let designIdx = designs.findIndex(design => design.fields.name === value.value);

              if (designIdx < 0) { categoryMatch = categoryMatch || false; } else { categoryMatch = categoryMatch || (designs[designIdx].productIds() && designs[designIdx].productIds().includes(this.fields.id)); }
              break;
            case 'Height':
              categoryMatch = categoryMatch || (this.height() >= value.value[0] && (this.height() < value.value[1] || value.value[1] < 0));
              break;
            case 'Width':
              categoryMatch = categoryMatch || (this.width() >= value.value[0] && (this.width() < value.value[1] || value.value[1] < 0));
              break;
            case 'Depth':
              categoryMatch = categoryMatch || (this.depth() >= value.value[0] && (this.depth() < value.value[1] || value.value[1] < 0));
              break;
            case 'Style':
              // TODO: How can we find if a product matches a category?
              break;
            case 'On Sale':
              categoryMatch = categoryMatch || ((value.value === 'sale' && this.isDiscounted()) || (value.value === 'notSale' && !this.isDiscounted()));
              break;
            default:
              // this shouldn't happen, but could if more filter types are added and not implemented here
              console.error('Uknown filter type encountered when filtering (' + key + ') It probably was added to filter types but not supported in filtering logic');
              break;
          }
        });

        match = match && categoryMatch;
      }

      // break early if we're excluded because of a category
      if (!match) { return false; }
    });

    return match;
  };

  // this is a fairly naive search method, we really want to be using the BE for searches
  doesProductMatchSearch = (searchValue) => {
    if (!searchValue) { return true; }

    let words = searchValue.toLowerCase().split(' ');

    if (this.fields.source.toLowerCase().includes(searchValue)) { // require full match for brands
      return true;
    }

    for (let i = 0; i < words.length; ++i) {
      let word = new RegExp('\\b' + words[i] + '\\b', 'i');

      if (this.fields.name.search(word) !== -1) { return true; }

      if (this.fields.types.some(type => { return type.search(word) !== -1; })) { return true; }

      if (this.fields.tags.some(tag => { return tag.search(word) !== -1; })) { return true; }

      for (let k = 0; k < this.fields.types.length; ++k) {
        if (words[i].includes(this.fields.types[k]) && (words[i].length - this.fields.types[k].length < 4)) { return true; }
      }

      for (let l = 0; l < this.fields.tags.length; ++l) {
        if (words[i].includes(this.fields.tags[l]) && (words[i].length - this.fields.tags[l].length < 4)) { return true; }
      }
    }

    return false;
  };

  // =======
  // GETTERS
  // =======
  /** Returns the id for this product. */
  id () { return this.fields.id; }

  /** Returns the name for this product. */
  name () { return this.fields.name; }

  /** Returns the images for this product. */
  images () { return this.fields.images; }

  /** Returns the image src for 0-based image index.
   * Empty string is returned if no image exists for the supplied index. */
  imageSrcAt (index) { return get(this.fields, `images[${index}].filename`, ''); }

  /** Returns the first image src for this product. */
  firstImageSrc () { return this.imageSrcAt(0); }

  /** Returns the first non-empty, non-null image src for thie product. */
  firstRealImageSrc () {
    let images = get(this.fields, `images`, '');

    let filename = '';

    images.forEach(image => {
      if (!filename && image.filename) { filename = image.filename; }
    });

    return filename;
  }

  /** Returns the number of images for this product. */
  imageCount () { return this.fields.images.length; }

  /** Returns whether or not this product has 3D assets. */
  has3dAssets () { return this.fields.has3dAssets; }

  /** Returns true if this is a favorite product. */
  isFavorite () { return this.fields.isFavorite; }

  /* Returns true if product has a badge while flags related to new UI badges on */
  hasBadge () {
    return this.isDiscounted() || this.isNew();
  }

  /*
  Returns badge's name if the product has one.
  In case a product has several badges, the order of priority is set as:
  1.Sale, 2.New
  */
  getBadgeName () {
    if (this.isExactMatch()) {
      return PRODUCT_BADGE_EXACTMATCH;
    } else if (this.isStandIn()) {
      return PRODUCT_BADGE_STANDIN;
    } else if (this.isDiscounted()) {
      return PRODUCT_BADGE_SALE;
    } else if (this.isNew()) {
      return PRODUCT_BADGE_NEW;
    } else {
      return '';
    }
  }

  /*
  Returns badge's color if the product has one
  */
  getBadgeColor () {
    if (this.isExactMatch() || this.isStandIn()) {
      return dawnPink;
    } else if (this.isDiscounted()) {
      return red;
    } else if (this.isNew()) {
      return black;
    } else {
      return '';
    }
  }

  /** Returns true if the product is currently discounted (on sale). */
  isDiscounted () {
    return this.currentPrice() < this.regularPrice();
  }

  /** Returns true if the product has a direct vendor price higher than current price. */
  isStrikethroughPriced () {
    return this.isDVProduct() && this.modsySavings() > 0 && this.vendorPrice();
  }

  /**
   * Getters related to PDP and productWidget prices
   */
  getCrossedPrice = () => {
    const crossedPrice = this.isStrikethroughPriced()
      ? this.vendorDisplayedPrice()
      : this.isDiscounted()
        ? this.regularDisplayedPrice()
        : null
    ;
    return crossedPrice;
  }

  getSavingAmount = () => {
    const savingAmount = this.isStrikethroughPriced()
      ? this.modsySavingsDisplayedAmount()
      : this.isDiscounted()
        ? this.discountDisplayedAmount()
        : 0
    ;

    return savingAmount;
  }

  getSavingPercent = () => {
    const savingPercent = this.isStrikethroughPriced()
      ? this.modsySavingsPercent()
      : this.isDiscounted()
        ? this.discountPercent()
        : 0
    ;

    return savingPercent;
  }

  /** Returns true if the product is new. */
  isNew () {
    return this.fields.tags.includes('New');
  }

  /** Returns whether the product is sellable */
  isSellable () {
    return this.fields.sourceStatus !== 'Restricted';
  }

  /** Returns true if the product is a direct vendor product. */
  isDVProduct () {
    return this.fields.vendorType === 'Dropship';
  }

  /** Returns the discount amount as a number. */
  discountAmount () {
    return Math.max(this.regularPrice() - this.currentPrice(), 0);
  }

  /** Returns the discount amount as a display suitable string. */
  discountDisplayedAmount () {
    const discount = this.regularPrice() - this.currentPrice();
    return discount.toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD'
    });
  }

  /** Returns whether this product supports variants by attribute */
  hasAttributeVariants () {
    return !!this.fields.variantAttributes && !isEmpty(this.fields.variantAttributes);
  }

  isFreeShipping () {
    return this.fields.isFreeShipping;
  }

  /** Returns whether this product is an unsellable material */
  isMaterial () {
    return UNSELLABLE_TYPES.includes(this.fields.mainType);
  }

  isPromoExcluded () {
    return this.fields.internalTags.includes('Exclude Promo');
  }

  externalId () {
    return this.fields.externalId;
  }

  /** Returns the type of variant attributes supported by this product */
  getVariantAttributeTypes () {
    return this.hasAttributeVariants() ? Object.keys(this.fields.variantAttributes) : [];
  }

  variantAttributes () {
    return this.fields.variantAttributes;
  }

  primaryVariantAttribute () {
    return this.fields.primaryVariantAttribute;
  }

  getQuantityMultiple () {
    return this.fields.quantityMultiple;
  }

  /** Returns the variant hash for this product */
  variantHash () { return this.fields.variantHash; }

  /** Returns whether we have already loaded the variants from the BE */
  variantsLoaded () { return this.fields.variantsLoaded; }

  /** Called when we receive all variants from the BE */
  setVariantsLoaded (loaded) { this.fields.variantsLoaded = loaded; }

  /** Returns the vendor type for this product */
  vendorType () { return this.fields.vendorType; }

  /** Returns the vendor price for this product. */
  vendorPrice () { return this.fields.vendorPrice; }

  /** Returns the vendor price as a display suitable string. */
  vendorDisplayedPrice () {
    if (this.vendorPrice() > 0) {
      return this.vendorPrice().toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD'
      });
    } else { return null; }
  }

  /** Returns the direct vendor modsy savings (vendorPrice - currentPrice). */
  modsySavings () { return this.fields.modsySavings; }

  /** Returns modsy savings as a display suitable string. */
  modsySavingsDisplayedAmount () {
    if (this.modsySavings() > 0) {
      return this.modsySavings().toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD'
      });
    } else { return null; }
  }

  /** Returns modsy savings as a percentage of vendorPrice. */
  modsySavingsPercent () {
    if (this.modsySavings() > 0 && this.vendorPrice() > 0) {
      return Math.round(100 * this.modsySavings() / this.vendorPrice());
    } else { return null; }
  }

  /** Returns discount savings as a percentage of regularPrice. */
  discountPercent () {
    if (this.discountAmount() > 0 && this.regularPrice() > 0) {
      return Math.round(100 * this.discountAmount() / this.regularPrice());
    } else { return null; }
  }

  /** Returns the current price for this product. */
  currentPrice () { return this.fields.currentPrice; }

  /** Returns the current price as a display suitable string. */
  currentDisplayedPrice () {
    const currentPrice = this.currentPrice();
    if (currentPrice !== null && currentPrice >= 0) {
      return currentPrice.toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD'
      });
    } else { return null; }
  }

  /** Returns the regular price for this product. */
  regularPrice () { return this.fields.regularPrice; }

  /** Returns the regular price as a display suitable string. */
  regularDisplayedPrice () {
    const regularPrice = this.regularPrice();
    if (regularPrice !== null && regularPrice >= 0) {
      return regularPrice.toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD'
      });
    } else { return null; }
  }

  /** Returns the retailer source for this product. */
  retailer () { return this.fields.source; }

  /** Returns true if the product retailer is one of Modsy's active partner. */
  isFromActivePartner () {
    const modsyActivePartners = ['CB2', 'Crate & Barrel'];
    return modsyActivePartners.includes(this.fields.source);
  }

  /** Returns the product url. */
  url () { return this.fields.url; }

  /** Returns the product tags. */
  tags () { return this.fields.tags; }

  /** Returns the product types. */
  types () { return this.fields.types; }

  /** Returns the product material. */
  material () { return this.fields.material; }

  /** Returns the product color. */
  color () { return this.fields.color; }

  /** Returns vendor's special messaging set in warehouse admin. */
  specialMessaging () { return this.fields.specialMessaging; }

  /** Returns the product description. */
  description () { return this.fields.description; }

  /** Returns the product description without (urls). */
  descriptionWithoutLinks () {
    const content = this.fields.description;
    // eslint-disable-next-line
    let noUrls = content.replace(/( ?\( ?)?(\b(https?|ftp|file):\/\/[-A-Z0-9+&@#\/%?=~_|!:,.;]*[-A-Z0-9+&@#\/%=~_|)])/ig, '') // detect urls
    // eslint-disable-next-line
    return noUrls.replace(/<\/?[\w\s="/.':;#-\/\?]+>/ig, ' '); // detect html tags
  }

  /** Returns true if the product is out of stock. */
  isOutOfStock () { return this.fields.isOutOfStock; }

  /** Returns the UI Messaging for the product, including availability statuses. */
  uiMessaging () { return this.fields.uiMessaging; }

  /** Returns true if the product is discontinued. */
  isDiscontinued () { return this.isOutOfStock() && this.availability() === PRODUCT_AVAILABILITY_STATUSES.DISCONTINUED; }

  /** Returns true if the product is backordered. */
  isBackOrdered () { return this.availability() === PRODUCT_AVAILABILITY_STATUSES.BACKORDER; }

  /** Returns true if the product is made to order. */
  isMadeToOrder () { return this.availability() === PRODUCT_AVAILABILITY_STATUSES.MADE_TO_ORDER; }

  /** Returns the product availability. */
  availability () { return this.fields.availability; }

  /** Returns the product availability info. */
  availabilityInfo () { return this.fields.availabilityInfo; }

  /** Get product estimated restock date if the product is backordered and availabity info is present */
  getEstimatedRestockDate () {
    // Days after availability date for estimated restock quote
    const restockLag = 14;
    const availabilityInfo = this.availabilityInfo();

    if (this.isBackOrdered() && availabilityInfo) {
      let availabilityDate = new Date(availabilityInfo);
      if (isValidDate(availabilityDate)) {
        availabilityDate.setDate(availabilityDate.getDate() + restockLag);
        return availabilityDate;
      }
    }
    return null;
  }

  /** Returns product dimension info. */
  depth () { return this.fields.depth; }
  hasDepth () { return this.depth() > 0; }

  height () { return this.fields.height; }
  hasHeight () { return this.height() > 0; }

  width () { return this.fields.width; }
  hasWidth () { return this.width() > 0; }

  volume () {
    const depthVal = this.hasDepth() ? this.fields.depth : 1;
    const heightVal = this.hasHeight() ? this.fields.height : 1;
    const widthVal = this.hasWidth() ? this.fields.width : 1;
    return depthVal * heightVal * widthVal;
  }

  /** Returns the warehouse product response from server. */
  warehouseProduct () { return this.fields.whProduct; }

  pricePoint () { return this.fields.pricePoint; }

  styleTag () { return this.fields.styleTag; }

  genomeTag () { return this.fields.genomeTag; }

  /** Returns true if product is tagged as Kid-Friendly attribute **/
  isKidFriendly () { return this.tags().includes('Kid-Friendly'); }

  /** Returns true if product is tagged with Pet-Friendly attribute **/
  isPetFriendly () { return this.tags().includes('Pet-Friendly'); }

  /** Returns true if product is tagged with Modsy Assortment catalog **/
  isModsyAssortment () { return this.tags().includes('Modsy Assortment'); }

  /** Returns true if product is too big to be swapped with current selected hotspot **/
  isTooBig () { return this.fields.isTooBig; }

  displayedShippingCost () {
    if (this.shippingCost()) {
      return this.shippingCost().toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD'
      });
    }

    return null;
  }

  hasFreeShipping () { return this.shippingCost() !== null && this.shippingCost() === 0; }

  shippingCost () { return this.fields.shippingCost; }

  shippingQuote () { return this.fields.shippingQuote; }

  shippingPolicy () { return this.fields.shippingPolicy; }

  additionalInfo () { return this.fields.additionalInfo; }

  mainType () { return this.fields.mainType; }

  customPropType () { return this.fields.customPropType; }

  isExactMatch () { return this.fields.customPropType === PROP_TYPE_CUSTOM_PRODUCT; }

  isStandIn () { return this.fields.customPropType === PROP_TYPE_ALTERNATIVE_PRODUCT; }

  getSegmentProperties () {
    return {
      color: this.color(),
      genome: this.genomeTag(),
      has_3d_assets: this.has3dAssets(),
      id: this.id(),
      is_assortment: this.isModsyAssortment(),
      is_kid_friendly: this.isKidFriendly(),
      is_on_sale: this.isDiscounted(),
      is_pet_friendly: this.isPetFriendly(),
      name: this.name(),
      price: this.regularPrice(),
      price_point: this.pricePoint(),
      promo_price: this.isDiscounted() && this.currentPrice(),
      type: this.mainType(),
      vendor: this.retailer(),
      vendor_price: this.vendorPrice(),
      vendor_type: this.vendorType()
    };
  }
}

export default Product;
